
import { Plus, Trash2, ArrowRight } from 'lucide-react';
import { Button } from '@/components/atoms/Button';
import { Input } from '@/components/atoms/Input';
import type { ActivityFormData } from '../ActivityEditorPage.types';
import styles from './TypeSpecificFields.module.scss';

interface MatchingFieldsProps {
  pairs: Array<{ left: string; right: string }>;
  onChange: (updates: Partial<ActivityFormData>) => void;
  error?: string;
}

export function MatchingFields({ pairs, onChange, error }: MatchingFieldsProps) {
  const handlePairChange = (index: number, side: 'left' | 'right', value: string) => {
    const newPairs = [...pairs];
    const currentPair = newPairs[index] || { left: '', right: '' };
    newPairs[index] = {
      left: side === 'left' ? value : currentPair.left,
      right: side === 'right' ? value : currentPair.right,
    };
    onChange({ pairs: newPairs });
  };

  const handleAddPair = () => {
    onChange({ pairs: [...pairs, { left: '', right: '' }] });
  };

  const handleRemovePair = (index: number) => {
    if (pairs.length <= 2) {
      return; // Minimum 2 pairs required
    }
    const newPairs = pairs.filter((_, i) => i !== index);
    onChange({ pairs: newPairs });
  };

  return (
    <div className={styles.typeFields}>
      <h3 className={styles.typeFields__title}>Pares para emparejar</h3>
      <p className={styles.typeFields__description}>
        Crea pares de elementos que los estudiantes deberán relacionar. Añade al menos 2 pares.
      </p>

      <div className={styles.typeFields__list}>
        {pairs.map((pair, index) => (
          <div key={index} className={styles.typeFields__pairItem}>
            <div>
              <Input
                value={pair.left}
                onChange={(e) => handlePairChange(index, 'left', e.target.value)}
                placeholder={`Elemento izquierdo ${index + 1}`}
              />
            </div>

            <div className={styles.typeFields__pairArrow}>
              <ArrowRight size={20} />
            </div>

            <div>
              <Input
                value={pair.right}
                onChange={(e) => handlePairChange(index, 'right', e.target.value)}
                placeholder={`Elemento derecho ${index + 1}`}
              />
            </div>

            <Button
              type="button"
              variant="ghost"
              size="sm"
              leftIcon={<Trash2 />}
              onClick={() => handleRemovePair(index)}
              disabled={pairs.length <= 2}
              aria-label={`Eliminar par ${index + 1}`}
            >
              <span className="sr-only">Eliminar</span>
            </Button>
          </div>
        ))}
      </div>

      <Button
        type="button"
        variant="outline"
        size="sm"
        leftIcon={<Plus />}
        onClick={handleAddPair}
        className={styles.typeFields__addButton}
      >
        Añadir par
      </Button>

      {error && <span className={styles.typeFields__error}>{error}</span>}
    </div>
  );
}
